import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/toast';
import {
  setClientErrorReporter,
  type ClientErrorContext,
} from '@/lib/api-fetch';
import { useTenant } from '@/contexts/TenantContext';
import { useHomeOrgId } from '@/lib/api/memberships';

const REPORT_RATE_LIMIT_MS = 10_000;

/**
 * W102-F1-S3 — surfaces the W100 resolveActiveOrg 403 codes (and the
 * AuthGuard 503) as helpful toasts.
 *
 * Without this bridge, a stale `X-Org-Id` (e.g. the user's persisted
 * activePartnerId pointing at an org they lost access to since the
 * last session) would manifest as silent "Forbidden" failures with
 * no path to recovery. The W102-F1-S2 `useActiveOrgIdSync` hook
 * proactively prevents this on the SPA side, but the bridge is the
 * backstop: if the backend still returns one of these codes, the
 * user sees an actionable message + auto-reset where applicable.
 *
 * Codes handled:
 *
 *   | Code (HTTP)                    | UX                                                    |
 *   |--------------------------------|-------------------------------------------------------|
 *   | ORG_NOT_IN_MEMBERSHIPS (403)   | Toast + auto-reset activePartnerId to homeOrgId       |
 *   | NO_HOME_ORG (403)              | Toast suggesting picking an org from the switcher     |
 *   | NO_ACTIVE_ORG (403)            | Toast saying "contact your partner admin"             |
 *   | CROSS_ORG_RESOURCE (403)       | Navigate to /errors/cross-org/:requiredOrgId (W102-F3-S2) |
 *   | CORE_USERS_ME_UNAVAILABLE (503)| Toast "Core unreachable; retry shortly"               |
 *
 * Rate-limited per-code to one toast per `REPORT_RATE_LIMIT_MS` so a
 * burst of failing requests doesn't spam the UI.
 *
 * Mounted once in AppShell adjacent to `<ServerErrorBridge />`.
 */
export function ActiveOrgErrorBridge(): null {
  const { t } = useTranslation();
  const { setActivePartner } = useTenant();
  const homeOrgId = useHomeOrgId();
  const navigate = useNavigate();
  const location = useLocation();
  const lastSeenRef = useRef<Map<string, number>>(new Map());

  // Keep `location.pathname + search` accessible inside the reporter
  // callback without re-registering on every navigation (re-register
  // would briefly drop the reporter, which races with in-flight fetches).
  const currentPathRef = useRef(location.pathname + location.search);
  useEffect(() => {
    currentPathRef.current = location.pathname + location.search;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const unregister = setClientErrorReporter((ctx: ClientErrorContext) => {
      const code = typeof ctx.envelope?.code === 'string' ? ctx.envelope.code : undefined;
      if (!code) return;

      const now = Date.now();
      const last = lastSeenRef.current.get(code) ?? 0;
      if (now - last < REPORT_RATE_LIMIT_MS) return;
      lastSeenRef.current.set(code, now);

      switch (code) {
        case 'ORG_NOT_IN_MEMBERSHIPS': {
          // Auto-recover: reset to homeOrgId so the next request goes
          // through. The toast still fires so the user knows what
          // happened (e.g. they got the link from a colleague at
          // another org).
          if (homeOrgId) setActivePartner(homeOrgId);
          toast.error(
            t('org.error.orgNotInMemberships.title', {
              defaultValue:
                'Organisation not in your memberships',
            }),
            {
              description: t('org.error.orgNotInMemberships.description', {
                defaultValue:
                  homeOrgId
                    ? 'Switched back to your default organisation.'
                    : 'Pick an organisation you have access to from the switcher.',
              }),
              duration: 6000,
            },
          );
          break;
        }
        case 'NO_HOME_ORG': {
          toast.error(
            t('org.error.noHomeOrg.title', {
              defaultValue: 'No default organisation set',
            }),
            {
              description: t('org.error.noHomeOrg.description', {
                defaultValue:
                  'Pick an organisation from the switcher in the top bar.',
              }),
              duration: 8000,
            },
          );
          break;
        }
        case 'NO_ACTIVE_ORG': {
          toast.error(
            t('org.error.noActiveOrg.title', {
              defaultValue: 'No organisation membership',
            }),
            {
              description: t('org.error.noActiveOrg.description', {
                defaultValue:
                  'Your account is being set up. Contact your partner admin to grant access.',
              }),
              duration: 10_000,
            },
          );
          break;
        }
        case 'CROSS_ORG_RESOURCE': {
          // W102-F3-S2: user pasted (or clicked) a deep link whose
          // resource lives in a different org. Navigate to the
          // recovery page with the required org id + the original
          // pathname so the page can switch + redirect them back.
          const requiredOrgId =
            typeof ctx.envelope?.requiredOrgId === 'string'
              ? ctx.envelope.requiredOrgId
              : undefined;
          if (!requiredOrgId) {
            // Backend didn't include the required org id — fall back
            // to a generic toast rather than redirecting nowhere.
            toast.error(
              t('org.error.crossOrgResource.titleNoId', {
                defaultValue: 'This resource belongs to another organisation',
              }),
              { duration: 8000 },
            );
            break;
          }
          // Skip the redirect if the user is already on the recovery
          // page (e.g. they tapped Retry on a CTA that re-fired the
          // failing request) — would otherwise loop.
          const here = currentPathRef.current;
          if (here.startsWith('/errors/cross-org/')) break;
          const returnTo = encodeURIComponent(here);
          navigate(
            `/errors/cross-org/${encodeURIComponent(requiredOrgId)}?returnTo=${returnTo}`,
          );
          break;
        }
        case 'CORE_USERS_ME_UNAVAILABLE': {
          toast.error(
            t('org.error.coreUsersMeUnavailable.title', {
              defaultValue: 'Unable to load your account',
            }),
            {
              description: t('org.error.coreUsersMeUnavailable.description', {
                defaultValue:
                  'There was a problem reaching the user-account service. Retry shortly.',
              }),
              duration: 8000,
            },
          );
          break;
        }
        default:
          // Not one of ours — let other handlers / callers deal with it.
          // Reset the rate-limit entry so an unrelated 4xx doesn't
          // starve future calls.
          lastSeenRef.current.delete(code);
          break;
      }
    });
    return () => {
      unregister();
    };
  }, [t, setActivePartner, homeOrgId, navigate]);

  return null;
}
